import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Trash2, Pencil } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { TextField, Select } from '@components/base/inputs';
import { Table } from '@components/base/data';
import { HStack, VStack } from '@components/base/layout';
import { Alert, Modal, Spinner, useToast } from '@components/base/feedback';
import { tenantsService } from '../../api/tenantsService';
import { usersService } from '../../api/usersService';
import type { Tenant } from '../../api/tenantsService';
import type { User } from '../../api/usersService';

interface TenantUsersTabProps {
  readonly tenant: Tenant;
}

export function TenantUsersTab({ tenant }: TenantUsersTabProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const tenantId = tenant.slug;

  // ---- Add User Modal State ----
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userRole, setUserRole] = useState('');

  // ---- Remove User State ----
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);

  // ---- Edit Role State ----
  const [editTarget, setEditTarget] = useState<User | null>(null);
  const [editRole, setEditRole] = useState('');

  // ---- Queries ----

  const {
    data: tenantUsersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin', 'tenants', tenantId, 'users'],
    queryFn: () => tenantsService.listUsers(tenantId),
  });

  const { data: allUsersData, isLoading: allUsersLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => usersService.list(),
    enabled: addModalOpen,
  });

  // ---- Mutations ----

  const addUserMutation = useMutation({
    mutationFn: (data: { user_id: number; role?: string | null }) =>
      tenantsService.addUser(tenantId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'tenants', tenantId, 'users'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'tenants', tenantId],
      });
      toast({ variant: 'success', title: 'User added to tenant successfully.' });
      handleCloseAddModal();
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: 'Failed to add user',
        message:
          err instanceof Error ? err.message : 'An unexpected error occurred.',
      });
    },
  });

  const updateUserRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string | null }) =>
      tenantsService.updateUserRole(tenantId, userId, role),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'tenants', tenantId, 'users'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'tenants', tenantId],
      });
      toast({ variant: 'success', title: 'Role updated successfully.' });
      handleCloseEditModal();
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: 'Failed to update role',
        message:
          err instanceof Error ? err.message : 'An unexpected error occurred.',
      });
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: (userId: string) =>
      tenantsService.removeUser(tenantId, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'tenants', tenantId, 'users'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'tenants', tenantId],
      });
      toast({ variant: 'success', title: 'User removed from tenant successfully.' });
      setDeleteTarget(null);
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: 'Failed to remove user',
        message:
          err instanceof Error ? err.message : 'An unexpected error occurred.',
      });
    },
  });

  // ---- Derived Data ----

  const tenantUsers = tenantUsersData?.data ?? [];
  const allUsers = allUsersData?.data ?? [];

  const tenantUserIds = new Set(tenantUsers.map((u) => u.id));
  const availableUserOptions = allUsers
    .filter((u) => !tenantUserIds.has(u.id))
    .map((u) => ({
      value: String(u.id),
      label: `${u.name} (${u.email})`,
    }));

  // ---- Handlers ----

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setSelectedUserId('');
    setUserRole('');
    addUserMutation.reset();
  };

  const handleAddUser = () => {
    /* v8 ignore start */
    if (!selectedUserId) return;
    /* v8 ignore stop */
    addUserMutation.mutate({
      user_id: Number.parseInt(selectedUserId, 10),
      role: userRole.trim() || null,
    });
  };

  const handleConfirmRemove = () => {
    /* v8 ignore start */
    if (!deleteTarget) return;
    /* v8 ignore stop */
    removeUserMutation.mutate(String(deleteTarget.id));
  };

  const handleOpenEditModal = (user: User) => {
    // The backend pivot has a single `role` column; the row's `roles`
    // array surfaces all attached labels but v1 only edits the first.
    setEditRole(user.roles[0] ?? '');
    setEditTarget(user);
  };

  const handleCloseEditModal = () => {
    setEditTarget(null);
    setEditRole('');
    updateUserRoleMutation.reset();
  };

  const handleSaveRole = () => {
    /* v8 ignore start */
    if (!editTarget) return;
    /* v8 ignore stop */
    const trimmed = editRole.trim();
    updateUserRoleMutation.mutate({
      userId: String(editTarget.id),
      role: trimmed === '' ? null : trimmed,
    });
  };

  // ---- Loading State ----

  if (isLoading) {
    return (
      <VStack align="center" justify="center" gap={2} className="py-16">
        <Spinner size="lg" />
        <Text color="muted">Loading tenant users...</Text>
      </VStack>
    );
  }

  // ---- Error State ----

  if (error) {
    return (
      <Alert variant="error" title="Failed to load tenant users">
        {error instanceof Error
          ? error.message
          : 'An unexpected error occurred while loading tenant users.'}
      </Alert>
    );
  }

  // ---- Render ----

  return (
    <VStack gap={6}>
      {/* Header */}
      <HStack justify="between" align="center">
        <HStack gap={3} align="center">
          <Text as="h2" size="xl" weight="semibold">
            Tenant Users
          </Text>
          <Badge variant="info" size="sm">
            {tenantUsers.length}
          </Badge>
        </HStack>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Icon icon={UserPlus} size="sm" />}
          onClick={() => setAddModalOpen(true)}
        >
          Add User
        </Button>
      </HStack>

      {/* Users Table or Empty State */}
      {tenantUsers.length === 0 ? (
        <Alert variant="info" title="No users">
          No users assigned to this tenant yet.
        </Alert>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell>Role</Table.HeaderCell>
              <Table.HeaderCell align="right">Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {tenantUsers.map((user) => (
              <Table.Row key={user.id}>
                <Table.Cell>
                  <Text weight="medium">{user.name}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="sm" color="muted">
                    {user.email}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <HStack gap={1} wrap>
                    {user.roles.length > 0 ? (
                      user.roles.map((role) => (
                        <Badge key={role} variant="info" size="sm">
                          {role}
                        </Badge>
                      ))
                    ) : (
                      <Text size="sm" color="muted">
                        --
                      </Text>
                    )}
                  </HStack>
                </Table.Cell>
                <Table.Cell align="right">
                  <HStack gap={1} justify="end">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Change role for ${user.name}`}
                      leftIcon={<Icon icon={Pencil} size="sm" />}
                      onClick={() => handleOpenEditModal(user)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${user.name}`}
                      leftIcon={<Icon icon={Trash2} size="sm" color="error" />}
                      onClick={() => setDeleteTarget(user)}
                    />
                  </HStack>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {/* Add User Modal */}
      <Modal open={addModalOpen} onClose={handleCloseAddModal} size="sm">
        <Modal.Header title="Add User to Tenant" />
        <Modal.Body>
          <VStack gap={4}>
            {allUsersLoading ? (
              <VStack align="center" gap={2} className="py-4">
                <Spinner size="md" />
                <Text size="sm" color="muted">
                  Loading users...
                </Text>
              </VStack>
            ) : (
              <Select
                label="User"
                placeholder="Select a user"
                searchable
                options={availableUserOptions}
                value={selectedUserId}
                onChange={(value) => setSelectedUserId(value as string)}
              />
            )}
            <TextField
              label="Role"
              placeholder="Enter role (optional)"
              value={userRole}
              onChange={(e) => setUserRole(e.target.value)}
              helperText="Optional role assignment for the user in this tenant."
            />
            {addUserMutation.isError && (
              <Alert variant="error" title="Failed to add user">
                {addUserMutation.error instanceof Error
                  ? addUserMutation.error.message
                  : 'An unexpected error occurred.'}
              </Alert>
            )}
          </VStack>
        </Modal.Body>
        <Modal.Footer>
          <HStack gap={2} justify="end">
            <Button
              variant="secondary"
              onClick={handleCloseAddModal}
              disabled={addUserMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddUser}
              loading={addUserMutation.isPending}
              disabled={!selectedUserId}
            >
              Add User
            </Button>
          </HStack>
        </Modal.Footer>
      </Modal>

      {/* Edit Role Modal */}
      <Modal
        open={editTarget !== null}
        onClose={handleCloseEditModal}
        size="sm"
      >
        <Modal.Header title="Change Role" />
        <Modal.Body>
          <VStack gap={4}>
            <TextField
              label="Role"
              placeholder="Enter role"
              value={editRole}
              onChange={(e) => setEditRole(e.target.value)}
              helperText="Leave empty to reset to the default role ('member')."
            />
            {updateUserRoleMutation.isError && (
              <Alert variant="error" title="Failed to update role">
                {updateUserRoleMutation.error instanceof Error
                  ? updateUserRoleMutation.error.message
                  : 'An unexpected error occurred.'}
              </Alert>
            )}
          </VStack>
        </Modal.Body>
        <Modal.Footer>
          <HStack gap={2} justify="end">
            <Button
              variant="secondary"
              onClick={handleCloseEditModal}
              disabled={updateUserRoleMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveRole}
              loading={updateUserRoleMutation.isPending}
            >
              Save
            </Button>
          </HStack>
        </Modal.Footer>
      </Modal>

      {/* Remove User Confirmation Modal */}
      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        size="sm"
      >
        <Modal.Header title="Remove User from Tenant" />
        <Modal.Body>
          <Text>
            Are you sure you want to remove{' '}
            <Text as="span" weight="bold">
              {deleteTarget?.name}
            </Text>{' '}
            from this tenant? The user account itself will not be deleted.
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <HStack gap={2} justify="end">
            <Button
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
              disabled={removeUserMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={removeUserMutation.isPending}
              onClick={handleConfirmRemove}
            >
              Remove
            </Button>
          </HStack>
        </Modal.Footer>
      </Modal>
    </VStack>
  );
}
